import React, { useState, useEffect } from 'react';
import { Table, Badge, Modal, Button, Form, Card, Row, Col, Alert } from 'react-bootstrap';
import { bcpMatrixAPI, usersAPI } from '../services/api';
import { FaUserShield, FaEdit, FaTrash, FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';

const BCPMatrix = () => {
  const { isAdmin, isHR } = useAuth();
  const [bcpEntries, setBcpEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [myBackupDuties, setMyBackupDuties] = useState([]);
  const [activeBackups, setActiveBackups] = useState([]);
  const [usersOnLeave, setUsersOnLeave] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [formData, setFormData] = useState({
    primary_user_id: '',
    backup_user_id: '',
    role_description: '',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchBCPEntries();
    fetchMyBackupDuties();
    fetchActiveBackups();
    fetchUsersOnLeaveWithBackups();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBCPEntries = async () => {
    try {
      const response = await bcpMatrixAPI.getAll();
      setBcpEntries(response.data);
    } catch (error) {
      console.error('Error fetching BCP entries:', error);
    }
  };

  const fetchMyBackupDuties = async () => {
    try {
      const response = await bcpMatrixAPI.getMyBackupDuties();
      setMyBackupDuties(response.data);
    } catch (error) {
      console.error('Error fetching backup duties:', error);
    }
  };

  const fetchActiveBackups = async () => {
    try {
      const response = await bcpMatrixAPI.getActiveBackups();
      setActiveBackups(response.data);
    } catch (error) {
      console.error('Error fetching active backups:', error);
    }
  };

  const fetchUsersOnLeaveWithBackups = async () => {
    try {
      const response = await bcpMatrixAPI.getUsersOnLeaveWithBackups();
      setUsersOnLeave(response.data);
    } catch (error) {
      console.error('Error fetching users on leave:', error);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : `User #${userId}`;
  };

  const getUserDepartment = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.department || 'N/A';
  };

  const handleAddClick = () => {
    setFormData({
      primary_user_id: '',
      backup_user_id: '',
      role_description: '',
      is_active: true
    });
    setShowAddModal(true);
  };

  const handleEditClick = (entry) => {
    setSelectedEntry(entry);
    setFormData({
      primary_user_id: entry.primary_user_id,
      backup_user_id: entry.backup_user_id,
      role_description: entry.role_description || '',
      is_active: entry.is_active
    });
    setShowEditModal(true);
  };

  const handleCreate = async () => {
    if (!formData.primary_user_id || !formData.backup_user_id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please select both primary and backup users',
        confirmButtonColor: '#635bff'
      });
      return;
    }

    try {
      await bcpMatrixAPI.create(formData);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'BCP Matrix entry created successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      setShowAddModal(false);
      fetchBCPEntries();
      fetchMyBackupDuties();
      fetchActiveBackups();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to create BCP entry',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = {
        backup_user_id: formData.backup_user_id,
        role_description: formData.role_description,
        is_active: formData.is_active
      };
      await bcpMatrixAPI.update(selectedEntry.id, updateData);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'BCP Matrix entry updated successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      setShowEditModal(false);
      fetchBCPEntries();
      fetchMyBackupDuties();
      fetchActiveBackups();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to update BCP entry',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete the BCP Matrix entry',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#635bff',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await bcpMatrixAPI.delete(id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'BCP Matrix entry deleted successfully',
          confirmButtonColor: '#635bff',
          timer: 2000,
          showConfirmButton: false
        });
        fetchBCPEntries();
        fetchMyBackupDuties();
        fetchActiveBackups();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete BCP entry',
          confirmButtonColor: '#635bff'
        });
      }
    }
  };

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#0a0a0a',
              letterSpacing: '-0.02em',
              margin: 0
            }}>
              BCP Escalation Matrix
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: '8px 0 0 0'
            }}>
              Manage backup assignments for business continuity
            </p>
          </div>
          {(isAdmin || isHR) && (
            <Button
              onClick={handleAddClick}
              style={{
                backgroundColor: '#635bff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaPlus size={14} />
              Add Assignment
            </Button>
          )}
        </div>
      </div>

      {/* Active Backup Alert */}
      {activeBackups.length > 0 && (
        <Alert variant="warning" style={{ borderRadius: '8px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaExclamationTriangle size={20} />
            <div>
              <strong>Active Backup Duty!</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                You are covering for {activeBackups.length} team member{activeBackups.length > 1 ? 's' : ''} who {activeBackups.length > 1 ? 'are' : 'is'} currently on leave.
              </p>
            </div>
          </div>
        </Alert>
      )}

      {/* Users on Leave Today */}
      {usersOnLeave.length > 0 && (
        <Card style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          marginBottom: '24px'
        }}>
          <Card.Body style={{ padding: '24px' }}>
            <h5 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '16px'
            }}>
              Team Members on Leave Today
            </h5>
            <Table responsive hover style={{ marginBottom: 0 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Employee</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Department</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Leave Type</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Leave Period</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Backup Person</th>
                </tr>
              </thead>
              <tbody>
                {usersOnLeave.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>{item.user_name}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{item.department || 'N/A'}</td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <Badge bg="info">{item.leave_type}</Badge>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                      {new Date(item.start_date).toLocaleDateString()} - {new Date(item.end_date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      {item.backup_user_name ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FaUserShield style={{ color: '#635bff' }} />
                          <span style={{ fontWeight: '500', color: '#374151' }}>{item.backup_user_name}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#dc3545', fontSize: '13px' }}>No backup assigned</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* My Backup Duties */}
      {myBackupDuties.length > 0 && (
        <Card style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          marginBottom: '24px'
        }}>
          <Card.Body style={{ padding: '24px' }}>
            <h5 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '16px'
            }}>
              My Backup Responsibilities
            </h5>
            <Table responsive hover style={{ marginBottom: 0 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Primary Person</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Department</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Role/Responsibilities</th>
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {myBackupDuties.map((entry) => {
                  const isOnLeave = activeBackups.some(ab => ab.id === entry.id);
                  return (
                    <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: isOnLeave ? '#fff9e6' : 'transparent' }}>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        {getUserName(entry.primary_user_id)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                        {getUserDepartment(entry.primary_user_id)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                        {entry.role_description || 'N/A'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {isOnLeave ? (
                          <Badge bg="warning" style={{ fontSize: '12px' }}>Active - On Leave</Badge>
                        ) : (
                          <Badge bg="secondary" style={{ fontSize: '12px' }}>Standby</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* All BCP Entries */}
      <Card style={{
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
      }}>
        <Card.Body style={{ padding: '24px' }}>
          <h5 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '16px'
          }}>
            All Backup Assignments
          </h5>
          <Table responsive hover style={{ marginBottom: 0 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Primary Person</th>
                <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Department</th>
                <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Backup Person</th>
                <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Role/Responsibilities</th>
                <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Status</th>
                {(isAdmin || isHR) && (
                  <th style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', padding: '12px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {bcpEntries.length === 0 ? (
                <tr>
                  <td colSpan={(isAdmin || isHR) ? 6 : 5} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                    No BCP assignments found
                  </td>
                </tr>
              ) : (
                bcpEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {getUserName(entry.primary_user_id)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                      {getUserDepartment(entry.primary_user_id)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaUserShield style={{ color: '#635bff' }} />
                        <span style={{ fontWeight: '500', color: '#374151' }}>{getUserName(entry.backup_user_id)}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                      {entry.role_description || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Badge bg={entry.is_active ? 'success' : 'secondary'} style={{ fontSize: '12px' }}>
                        {entry.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    {(isAdmin || isHR) && (
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEditClick(entry)}
                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px' }}
                          >
                            <FaEdit size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(entry.id)}
                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px' }}
                          >
                            <FaTrash size={12} />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '20px', fontWeight: '600' }}>Add BCP Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>Primary Person *</Form.Label>
              <Form.Select
                value={formData.primary_user_id}
                onChange={(e) => setFormData({ ...formData, primary_user_id: parseInt(e.target.value) })}
              >
                <option value="">Select Primary Person</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.department || 'No Dept'})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>Backup Person *</Form.Label>
              <Form.Select
                value={formData.backup_user_id}
                onChange={(e) => setFormData({ ...formData, backup_user_id: parseInt(e.target.value) })}
              >
                <option value="">Select Backup Person</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.department || 'No Dept'})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>Role/Responsibilities</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.role_description}
                onChange={(e) => setFormData({ ...formData, role_description: e.target.value })}
                placeholder="Describe the role and responsibilities..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#635bff', border: 'none' }}
            onClick={handleCreate}
          >
            Create Assignment
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '20px', fontWeight: '600' }}>Edit BCP Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>Primary Person</Form.Label>
              <Form.Control
                type="text"
                value={getUserName(formData.primary_user_id)}
                disabled
                style={{ backgroundColor: '#f3f4f6' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>Backup Person *</Form.Label>
              <Form.Select
                value={formData.backup_user_id}
                onChange={(e) => setFormData({ ...formData, backup_user_id: parseInt(e.target.value) })}
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.department || 'No Dept'})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>Role/Responsibilities</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.role_description}
                onChange={(e) => setFormData({ ...formData, role_description: e.target.value })}
                placeholder="Describe the role and responsibilities..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#635bff', border: 'none' }}
            onClick={handleUpdate}
          >
            Update Assignment
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BCPMatrix;
